/**
 * 场景卡片组件
 */

import React from 'react';
import { Card, Space, Tag, Checkbox, Typography } from 'antd';
import type { UsageScenario, ScriptType } from '../../types';

const { Text, Paragraph } = Typography;

// 剧本类型标签
const ScriptTag: React.FC<{ type: ScriptType }> = ({ type }) => {
    const config = {
        blue: { color: 'blue', label: '🔵 理性' },
        green: { color: 'green', label: '🟢 感性' },
        hybrid: { color: 'purple', label: '⚪ 混合' },
    };
    const { color, label } = config[type];
    return <Tag color={color}>{label}</Tag>;
};

interface ScenarioCardProps {
    scenario: UsageScenario;
    selected: boolean;
    onToggle: () => void;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({ scenario, selected, onToggle }) => {
    // 兼容处理
    const scriptType = scenario.recommended_script || scenario.copywriting_guidance?.recommended_script || 'hybrid';
    const description = scenario.sensory_description || scenario.description || '';
    const emotionalKeywords = scenario.emotional_keywords || scenario.copywriting_guidance?.emotional_keywords || [];

    return (
        <Card
            size="small"
            hoverable
            onClick={onToggle}
            style={{
                borderColor: selected ? '#52c41a' : undefined,
                backgroundColor: selected ? '#f6ffed' : undefined,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                    {/* 头部 */}
                    <div style={{ marginBottom: 8 }}>
                        <Space wrap>
                            <span style={{ fontSize: 20 }}>{scenario.icon || '📍'}</span>
                            <Text strong>{scenario.name}</Text>
                            {scenario.category && (
                                <Tag color="purple" style={{ fontSize: 10 }}>{scenario.category}</Tag>
                            )}
                            <ScriptTag type={scriptType} />
                        </Space>
                    </div>

                    {/* 场景上下文简要 */}
                    {scenario.context && (
                        <div style={{ marginBottom: 6, fontSize: 11, color: '#666' }}>
                            🕐 {scenario.context.time} · 📍 {scenario.context.location} · 💭 {scenario.context.trigger}
                        </div>
                    )}

                    {/* 五感描写 */}
                    <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 3, expandable: true }}
                        style={{ marginBottom: 8, fontSize: 13 }}
                    >
                        {description}
                    </Paragraph>

                    {/* 用户故事 */}
                    {scenario.user_story && (
                        <div style={{ marginBottom: 8, padding: 8, background: '#fafafa', borderRadius: 4, fontSize: 12 }}>
                            <Text type="secondary">📖 </Text>
                            <Text>{scenario.user_story.protagonist}: {scenario.user_story.situation}</Text>
                        </div>
                    )}

                    {/* 情感关键词 */}
                    {emotionalKeywords.length > 0 && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>情感关键词: </Text>
                            {emotionalKeywords.slice(0, 4).map((kw, i) => (
                                <Tag key={i} color="geekblue" style={{ fontSize: 11 }}>{kw}</Tag>
                            ))}
                        </div>
                    )}

                    {/* 适用人群 */}
                    {scenario.target_audiences && scenario.target_audiences.length > 0 && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>适用人群: </Text>
                            {scenario.target_audiences.slice(0, 3).map((aud, i) => (
                                <Tag key={i} style={{ fontSize: 11 }}>{aud}</Tag>
                            ))}
                        </div>
                    )}

                    {/* 文案指导 */}
                    {scenario.copywriting_guidance && (
                        <div style={{ marginTop: 8, padding: 8, background: '#fff7e6', borderRadius: 4 }}>
                            <Text style={{ fontSize: 11, color: '#fa8c16' }}>💡 文案指导: </Text>
                            <div style={{ fontSize: 11, marginTop: 4 }}>
                                <div>• 核心痛点: {scenario.copywriting_guidance.pain_to_address}</div>
                                <div>• 突出利益: {scenario.copywriting_guidance.benefit_to_highlight}</div>
                                {scenario.copywriting_guidance.opening_hook && (
                                    <div>• 钩子文案: "{scenario.copywriting_guidance.opening_hook}"</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 兼容旧版的文案切入 */}
                    {!scenario.copywriting_guidance && scenario.copywriting_angle && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                💡 文案切入: {scenario.copywriting_angle}
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default ScenarioCard;
